import { useState, useCallback } from 'react';

export interface UseZoomReturn {
  zoomLevel: number;
  zoomIn: () => void;
  zoomOut: () => void;
  setZoom: (level: number) => void;
  resetZoom: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

/**
 * Custom hook for managing zoom functionality
 * Provides zoom controls with min/max bounds
 */
export const useZoom = (
  initialZoom: number = 100,
  minZoom: number = 50,
  maxZoom: number = 200,
  step: number = 25
): UseZoomReturn => {
  const [zoomLevel, setZoomLevel] = useState(initialZoom);

  /**
   * Increases zoom level by step amount
   */
  const zoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + step, maxZoom));
  }, [maxZoom, step]);

  /**
   * Decreases zoom level by step amount
   */
  const zoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - step, minZoom));
  }, [minZoom, step]);

  /**
   * Sets zoom to specific level
   */
  const setZoom = useCallback((level: number) => {
    setZoomLevel(Math.max(minZoom, Math.min(maxZoom, level)));
  }, [minZoom, maxZoom]);

  /**
   * Resets zoom to initial level
   */
  const resetZoom = useCallback(() => {
    setZoomLevel(initialZoom);
  }, [initialZoom]);

  return {
    zoomLevel,
    zoomIn,
    zoomOut,
    setZoom,
    resetZoom,
    canZoomIn: zoomLevel < maxZoom,
    canZoomOut: zoomLevel > minZoom
  };
};
